import React, { useEffect, useState } from 'react';
import { useTypedSelector } from 'store/index';
import { Icon, message } from '@tencent/spaui';
import { ListRender, useList } from '@tencent/ptp-fe-common';
import { Operator, OperatorRole } from 'model/operator';
import { OPERATOR_ROLE_MAP, LOGIN_TYPE_LIST } from 'config/constants';
import { fetchOperatorList, deleteOperator } from './services';
import OperatorEdit from './components/edit';
import { operatorColumnConfig } from './config';
import { report } from 'store/report';

const OPERATOR_LIMIT = 10;
const loginTypeMap: Record<string, string> = LOGIN_TYPE_LIST.reduce((map, v) => ({ ...map, [v.value]: v.label }), {});

const OperatorList: React.FC = () => {
  const loginUser = useTypedSelector((state) => state.user);
  const [total, setTotal] = useState(0);

  useEffect(() => {
    report({ event: 'agency_manage', payload: '成员管理' });
  }, []);

  const columnRender = (type: string, name: string, value: any, data: Operator) => {
    if (type === 'data') {
      switch (name) {
        case 'operator_role':
          return OPERATOR_ROLE_MAP[value as OperatorRole] || '未知';
        case 'login_type': {
          const { login_name, login_type } = data.logins || {};
          return `${loginTypeMap[login_type]}: ${login_name}`;
        }
        case 'operator_name': {
          if (value.length > 20) {
            return (
              <div title={value} style={{ cursor: 'pointer' }}>
                {value.slice(0, 20)}...
              </div>
            );
          }
          return value;
        }
      }
    }
    return value;
  };

  const listProps = useList({
    fetch: {
      action: fetchOperatorList,
      $onResponse: (data: any) => {
        setTotal(data?.list?.length || 0);
        return data;
      },
    },
    schema: (payloads: any, funcs: any) => {
      const isAdmin = payloads.loginUser?.user?.agency_role === OperatorRole.admin;
      const loginOperatorId = payloads.loginUser?.user?.operator_id;
      const isDisableAdd = payloads.total >= OPERATOR_LIMIT;
      return {
        toolbar: {
          left: [
            isAdmin && {
              component: 'Button',
              componentProps: {
                icon: <Icon name="plus-outlined" />,
                displayType: 'primary',
                children: '添加成员',
                disabled: isDisableAdd,
                tip: isDisableAdd ? `最多添加${OPERATOR_LIMIT}个成员` : '',
              },
              action: {
                type: 'dialog',
                props: {
                  config: {
                    title: '添加成员',
                    size: 'large',
                  },
                  body: OperatorEdit,
                  onSuccess: () => {
                    setTimeout(() => {
                      funcs.reload();
                    }, 2000);
                  },
                },
              },
            },
          ].filter(Boolean),
        },
        table: {
          columns: [
            ...operatorColumnConfig.map((v) => ({
              ...v,
              customRender: funcs.columnRender,
            })),
            {
              type: 'operation',
              title: '操作',
              actions: (data: Operator) => {
                const isAdminEdit = isAdmin && data.operator_role === OperatorRole.operator;
                const isOperatorEdit = !isAdmin && data.operator_id === loginOperatorId;
                return [
                  {
                    label: '编辑',
                    show: isAdminEdit || isOperatorEdit,
                    action: {
                      type: 'dialog',
                      props: {
                        config: {
                          title: '编辑成员',
                          size: 'large',
                        },
                        body: OperatorEdit,
                        payload: {
                          data: {
                            ...data,
                            login_type: data?.logins?.login_type,
                            login_name: data?.logins?.login_name,
                          },
                        },
                        onSuccess: () => {
                          setTimeout(() => {
                            funcs.reload();
                          }, 2000);
                        },
                      },
                    },
                  },
                  {
                    label: '删除',
                    show: isAdminEdit,
                    action: {
                      type: 'dialog',
                      props: {
                        config: {
                          title: `确定要删除 ${data.operator_name} 吗？`,
                        },
                        body: '删除后不可撤销',
                        onSuccess: async () => {
                          await deleteOperator({ operator_id: data.operator_id });
                          message.success('删除成员成功');
                          setTimeout(() => {
                            funcs.reload();
                          }, 2000);
                        },
                      },
                    },
                  },
                ];
              },
            },
          ].filter(Boolean),
        },
      };
    },
    funcs: {
      columnRender,
    },
    payloads: {
      loginUser,
      total,
    },
  });

  return (
    <div className="operator-info page-form-group">
      <div className="form-group-header">
        <h4>成员管理</h4>
      </div>
      <ListRender {...listProps} />
    </div>
  );
};

export default OperatorList;
